import merge from 'lodash/merge';
import { useInViewport } from 'ahooks';
import { SIDE_SEP_LENGTH } from './dataFlow';
import { If } from 'common/src/components/if';
import type { IChartProps } from 'bizcharts/lib/interface';
import type { ILegend } from 'bizcharts/lib/components/Legend';
import React, { useEffect, useRef, useState } from 'react';
import Loading from 'common/src/components/loading/loading';
import { ICapabilityProps, ChartColorType } from './interface';
import { breakTextByLimit } from 'common/src/components/text/break';
import { useBizcharts } from '@umu/umu-react-hooks';
import LoadingV2 from 'common/src/components/loadingV2/loading';

const tooltipDefault = {
    shared: true,
};

const selectLabelDefaultConfig = {
    color: '#1e6ee6',
    defaultColor: '#666666',
    isMultiSelect: false,
};

interface IProps extends ICapabilityProps {
    height?: number;
    color?: ChartColorType;
    // https://bizcharts.taobao.com/product/BizCharts4/category/62/page/79#grid
    axisGrid?: any;
    // https://bizcharts.taobao.com/product/BizCharts4/category/62/page/77#scale
    scale?: any;
    // https://bizcharts.taobao.com/product/BizCharts4/category/62/page/80#shared
    tooltip?: any;
    legendProps?: ILegend; // 图例属性
    intervalProps?: IChartProps;
    intervalChartProps?: IChartProps;
    compareChartType?: string;
    customTooltip?: null | ((xVal: string, info: any) => React.ReactNode);
}

const scaleDefault = {
    value: {
        min: 0,
        max: 100,
        tick: 5,
        ticks: [0, 20, 40, 60, 80, 100],
        formatter: (val: number) => {
            return `${val}`;
        },
    },
};
const defaultSliderProps = {
    padding: [20, 20, 0, 0],
    formatter: () => '',
    foregroundStyle: {
        fill: '#1e6ee6', // 网格线的颜色
    },
};
function IntervalChart(props: IProps) {
    const {
        data = [],
        height = 260,
        color = '#2197F3',
        preCls,
        chartProps,
        showInteraction = false,
        initSlider = 1,
        isShowSlider = false,
        hideTooltip = false,
        defaultSelected = [],
        scale = scaleDefault,
        selectLabelConfig,
        isSwitchOnLabelSelect = false,
        tooltip = tooltipDefault,
        customTooltip,
        onLabelSelect,
        formatterLabel,
        getAnnotationProps,
        legendProps,
        intervalProps,
        intervalChartProps,
        compareChartType = 'type',
        splitCount = SIDE_SEP_LENGTH,
    } = props;

    const interfaceRef = useRef(null);
    const labelSelectRef = useRef<string[]>([]);
    const [labelSelect, setLabelSelect] = useState<string[]>(defaultSelected);
    const [isShow, setIsShow] = useState(false);
    const [inViewport] = useInViewport(interfaceRef);
    const chartIns = useRef<any>(null);
    const [legendMap, setLegendMap] = useState<Record<string, boolean>>({});

    const labelConfig = merge(selectLabelDefaultConfig, selectLabelConfig);
    const defaultLabelConfig = {
        fontSize: 14,
        lineHeight: 20,
        width: 100,
        wordBreak: 'break-word',
        fill: labelConfig.defaultColor,
    };
    const onChangeSelectLabel = (info: string[]) => {
        setLabelSelect(info);
        onLabelSelect?.(info);
    };
    const onSelectChange = (id: string) => {
        if (!isSwitchOnLabelSelect || !id) {
            return;
        }
        let newLabelList = labelSelectRef.current;
        const isHas = newLabelList.includes(id);
        const isMulti = labelConfig.isMultiSelect;
        if (isHas) {
            newLabelList = isMulti ? newLabelList.filter((item) => item !== id) : [];
        } else {
            newLabelList = isMulti ? [...newLabelList, id] : [id];
        }
        labelSelectRef.current = newLabelList;
        onChangeSelectLabel(newLabelList);
    };
    const onAxisLabelClick = (ev: any) => {
        const id = ev.target?.cfg?.delegateObject?.item?.id;
        onSelectChange(id);
    };
    const onIntervalClick = (ev: any) => {
        const id = ev?.data?.data?.info?.id;
        onSelectChange(id);
    };

    const renderAnnotationMark = () => {
        if (getAnnotationProps) {
            return data.map((item: any, index: number) => {
                const markInfo = getAnnotationProps(item);
                return <Annotation.Html key={index} position={[item.label, item.value]} {...markInfo} />;
            });
        }
        return null;
    };

    const onAxisLabelMouseover = (ev: any) => {
        if (!isSwitchOnLabelSelect) {
            return;
        }
        // 修改颜色 - 选中
        ev.target.attr('fill', labelConfig.color);
    };

    const onAxisLabelMouseleave = (ev: any) => {
        if (!isSwitchOnLabelSelect) {
            return;
        }
        const id = ev.target?.cfg?.delegateObject?.item?.id;
        if (labelSelect.includes(id)) {
            return;
        }
        // 修改颜色 - 默认
        ev.target.attr('fill', labelConfig.defaultColor);
    };

    const onLegendChange: any = (ev: { item: any }) => {
        if (!chartIns.current) {
            return;
        }

        const { item } = ev;
        const { value } = item;
        const checked = !item.unchecked;
        legendMap[value] = checked;

        // 通过filter控制元素显示隐藏
        chartIns.current.filter(compareChartType, (val: string) => {
            const isShow = legendMap[val] !== false;
            legendMap[val] = isShow;
            return isShow;
        });
        setLegendMap({ ...legendMap });
        // 重新渲染，触发filter
        chartIns.current.render(true);
    };

    useEffect(() => {
        if (inViewport && !isShow) {
            setIsShow(true);
        }
    }, [inViewport]);

    useEffect(() => {
        onChangeSelectLabel([]);
    }, [data]);

    const BizChartsModules = useBizcharts();

    if (!BizChartsModules) {
        return <LoadingV2 />;
    }

    const { Chart, Axis, Tooltip, Interval, Legend, Slider, registerInteraction, Interaction, Annotation } =
        BizChartsModules;

    registerInteraction('active-umu-region', {
        start: [{ trigger: 'plot:mousemove', action: 'active-region:show', arg: { style: { fill: '#F5F5F5' } } }],
        end: [{ trigger: 'plot:mouseleave', action: 'active-region:hide' }],
    });

    return (
        <div className={preCls} ref={interfaceRef}>
            <If condition={!isShow}>
                <Loading />
            </If>
            <If condition={isShow}>
                <Chart
                    height={height}
                    data={data}
                    padding={[40, 80, isShowSlider ? 60 : 44, 80]}
                    autoFit
                    theme={{ maxColumnWidth: 40 }}
                    scale={scale}
                    onGetG2Instance={(chart: any) => {
                        chartIns.current = chart;
                    }}
                    onIntervalClick={onIntervalClick}
                    onAxisLabelClick={onAxisLabelClick}
                    onAxisLabelMouseover={onAxisLabelMouseover}
                    onAxisLabelMouseleave={onAxisLabelMouseleave}
                    {...chartProps}
                    {...intervalChartProps}
                >
                    <Legend {...legendProps} onChange={onLegendChange} />
                    <Tooltip {...tooltip} visible={!hideTooltip}>
                        {customTooltip}
                    </Tooltip>

                    <Interval position="label*value" {...intervalProps} color={color} />
                    <Axis
                        name="value"
                        label={{
                            style: defaultLabelConfig,
                        }}
                        grid={{
                            line: {
                                type: 'line', // 网格线类型 line circle polygon
                                style: {
                                    stroke: '#EEEEEE', // 网格线的颜色
                                    lineWidth: 1, // 网格线的宽度复制代码
                                },
                            },
                            alignTick: true,
                        }}
                    />
                    <Axis
                        name="label"
                        line={false}
                        tickLine={false}
                        label={{
                            autoHide: true,
                            autoEllipsis: false,
                            style: isSwitchOnLabelSelect
                                ? (_val, index) => {
                                      const itemInfo = data[index];
                                      return {
                                          ...defaultLabelConfig,
                                          fill: labelSelect.includes(itemInfo.label)
                                              ? labelConfig.color
                                              : labelConfig.defaultColor,
                                      };
                                  }
                                : defaultLabelConfig,
                            formatter(text) {
                                let label = formatterLabel ? formatterLabel(text) : text;
                                label = breakTextByLimit(label, 2, splitCount);
                                return label;
                            },
                        }}
                    />
                    <If condition={isShowSlider}>
                        <Slider start={0} end={initSlider} {...defaultSliderProps} />
                    </If>
                    <If condition={showInteraction}>
                        <Interaction type="active-umu-region" />
                    </If>
                    {renderAnnotationMark()}
                </Chart>
            </If>
        </div>
    );
}

export { IntervalChart };
